import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { goBack, lightImpactVibrate, onReady, postCTA, trackEvent } from '../../utils/appBridge'
import { getBottomPadding } from '../../utils/sessionStorage'
import { fetchRegularPlanAlertSheet } from './repo/regularPlanAlertSheetApi'
import {
  type RegularPlanAlertVariant,
  RegularPlanAlertVariantEnum,
} from './types'
import { LoadingSpinner } from '../../components/LoadingSpinner'
import { RegularPlanAlertSheetContent } from './RegularPlanAlertSheetContent'
import { EventNames } from '../../types/misc.types'

const VALID_VARIANTS: RegularPlanAlertVariant[] = Object.values(RegularPlanAlertVariantEnum)

function parseVariant(variant: string | undefined): RegularPlanAlertVariant {
  return variant && VALID_VARIANTS.includes(variant as RegularPlanAlertVariant)
    ? (variant as RegularPlanAlertVariant)
    : RegularPlanAlertVariantEnum.REGULAR_FUND_COMMISSIONS_SUMMARY_BOTTOM_SHEET
}

function getCTALabel(variant: RegularPlanAlertVariant): string {
  switch (variant) {
    case RegularPlanAlertVariantEnum.REGULAR_FUND_COMMISSIONS_BOTTOM_SHEET:
      return 'Unlock plan with Elite'
    case RegularPlanAlertVariantEnum.REGULAR_FUND_COMMISSIONS_GRAPH_BOTTOM_SHEET:
    case RegularPlanAlertVariantEnum.REGULAR_FUND_COMMISSIONS_SUMMARY_BOTTOM_SHEET:
    default:
      return 'Know more'
  }
}

export default function RegularPlanAlertSheetPage() {
  const [searchParams] = useSearchParams()

  const variant = parseVariant(searchParams.get('bottomSheetType') ?? undefined)

  const {
    data: sheetResponse,
    isLoading: isSheetLoading,
    isError: isSheetError,
    isSuccess: isSheetSuccess,
    refetch: refetchSheet,
  } = useQuery({
    queryKey: ['regularPlanAlertSheet', variant],
    queryFn: () => fetchRegularPlanAlertSheet(variant),
    retry: 1,
  })

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        responseName: variant,
        screenType: 'CONTENT_BOTTOM_SHEET',
      },
    })
  }, [])

  useEffect(() => {
    if (isSheetError || isSheetSuccess) {
      onReady()
    }
  }, [isSheetError, isSheetSuccess])

  if (isSheetLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px] w-full">
        <LoadingSpinner spinnerColor="#992617" />
      </div>
    )
  }

  if (isSheetError || !sheetResponse?.data) {
    return (
      <div className="font-[400] text-center text-[16px] text-[#992617] py-8 px-5 
      h-screen flex flex-col justify-center items-center">
        Unable to load. Please try again. <span onClick={() => {
          lightImpactVibrate()
          refetchSheet()
        }} className='underline cursor-pointer'>Retry</span>
      </div>
    )
  }

  return (

    <div className="pt-6 pb-1 h-full w-full relative">
      <div className="h-full pb-48 px-5 overflow-y-auto no-scrollbar">
        <div className="max-w-md mx-auto">
          <RegularPlanAlertSheetContent
            variant={variant}
            sheetData={sheetResponse?.data}
          />
        </div>
      </div>
      <div className="sticky bottom-0 left-0 right-0">
        <div
          style={{ paddingBottom: `${getBottomPadding() + 8}px` }}
          className="px-5 pt-1 bg-gradient-to-b flex flex-col from-[rgba(255,255,255,0)] via-[rgba(255,255,255,0.3)] to-[rgba(255,255,255,0.8)]"
        >
          <div className='bg-[#FFF2DF] px-5 pt-3 pb-[10px] rounded-[12px] -mb-4 flex justify-center items-center' >
            <div className="text-[13px] font-[400] text-[#CC5C2D] leading-[140%] mb-4 text-center max-w-[295px]">
              Your Regular to Direct plan is ready to help you switch to Direct funds, tax-efficiently.
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.ButtonTap,
                payload: {
                  buttonLabel: getCTALabel(variant),
                  eventId: variant,
                  screenType: 'CONTENT_BOTTOM_SHEET',
                },
              })
              const cta = sheetResponse?.data?.cta
              if (cta) {
                postCTA(cta)
              } else {
                goBack()
              }
            }}
            className="w-full py-2 px-4 bg-[#232323] text-white font-medium rounded-xl h-14 outline-none focus:outline-none"
          >
            {getCTALabel(variant)}
          </button>
        </div>
      </div>
    </div>

  )
}
